"use client";

// Wrapper visual do site (Topbar + main + Footer + WhatsApp flutuante)
// + providers de context (siteData, lang). Substitui o antigo MBMSite que
// também controlava roteamento via hash. Agora o Next App Router faz o
// roteamento; este shell só persiste em volta de qualquer página.
//
// `locale` é prop vinda do `app/[locale]/layout.jsx` (extraído de params).
// É a fonte de verdade do idioma — sem localStorage, sem persistência
// client-side. Trocar de idioma = router.push pra `/{outroLocale}/<path>`.

import { useEffect, useMemo } from "react";
import { useRouter, usePathname } from "next/navigation";
import { SiteDataProvider } from "../lib/site-context";
import { LangProvider } from "../lib/lang-context";
import { mapToPtPath } from "../lib/route-map";
import { isLocale, swapLocale, withLocale } from "../lib/i18n";
import { makeT } from "../lib/strings";
import { FloatingWhatsApp, Footer, Topbar } from "./mbm-components";
import CookieBanner from "./cookie-banner";

export default function SiteShell({ siteData, locale, children }) {
  const router = useRouter();
  const pathname = usePathname() ?? `/${locale}`;
  const lang = isLocale(locale) ? locale : "pt";

  const t = useMemo(() => makeT(siteData?.strings, lang), [siteData?.strings, lang]);

  // setLang: troca o segmento de locale e preserva o resto do path.
  // Próxima request o middleware/layout carrega o novo idioma e o
  // SiteShell re-renderiza com o lang atualizado.
  const setLang = (next) => {
    if (!isLocale(next) || next === lang) return;
    router.push(swapLocale(pathname, next));
  };

  // navigate: usado por componentes legados que recebem essa função e
  // navegam pra paths EN-style (/catalog, /contact). Aplica mapeamento
  // EN→PT + prefixo de locale.
  const navigate = (path) => {
    const target = withLocale(mapToPtPath(path), lang);
    router.push(target);
  };

  // Redireciona bookmarks legados do hash routing — `/#/contato` ou
  // `/#/contact` → `/{locale}/contato`. Roda só uma vez no boot client-side.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash;
    if (!hash || !hash.startsWith("#/")) return;
    const oldPath = hash.slice(1); // tira o `#`
    const target = withLocale(mapToPtPath(oldPath), lang);
    if (target && target !== window.location.pathname + window.location.search) {
      router.replace(target);
    } else {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SiteDataProvider value={siteData}>
      <LangProvider value={{ lang, setLang, t }}>
        <Topbar
          route={pathname}
          navigate={navigate}
          lang={lang}
          setLang={setLang}
          t={t}
        />
        <main data-screen-label={pathname}>{children}</main>
        <Footer t={t} lang={lang} navigate={navigate} />
        <FloatingWhatsApp />
        <CookieBanner />
      </LangProvider>
    </SiteDataProvider>
  );
}
